import {alpha, decomposeColor, getContrastRatio, hslToRgb, Theme} from "@mui/material/styles";

export interface SigningControlColors {
    border: string;
    hoverBorder: string;
    accent: string;
    focus: string;
    fill: string;
    onFill: string;
}

export interface SigningPanelColors {
    background: string;
    text: string;
    textSecondary: string;
    rule: string;
}

export interface SigningShape {
    control: number;
    card: number;
}

declare module '@mui/material/styles' {
    interface Theme {
        signingControls?: SigningControlColors;
        signingPanel?: SigningPanelColors;
        signingShape?: SigningShape;
    }
    interface ThemeOptions {
        signingControls?: SigningControlColors;
        signingPanel?: SigningPanelColors;
        signingShape?: SigningShape;
    }
}

const channels = (color: string) => decomposeColor(color.startsWith('hsl') ? hslToRgb(color) : color).values;

export const readableColor = (color?: string | null): string | undefined => {
    const value = color?.trim();
    if (!value) return undefined;
    try {
        channels(value);
        return value;
    } catch {
        return undefined;
    }
};

export const compositeColor = (color: string, background: string): string => {
    const foreground = channels(color);
    const behind = channels(background);
    const opacity = foreground[3] ?? 1;
    return `rgb(${foreground.slice(0, 3).map((value, index) => Math.round(value * opacity + behind[index] * (1 - opacity))).join(', ')})`;
};

const contrastOn = (color: string, backgrounds: string[]) => Math.min(...backgrounds.map(background => (
    getContrastRatio(compositeColor(color, background), background)
)));

export const contrastColor = (color: string, backgrounds: string[], minimum: number): string => {
    if (contrastOn(color, backgrounds) >= minimum) return color;

    const base = compositeColor(color, backgrounds[0]);
    const failing = backgrounds.filter(background => getContrastRatio(base, background) < minimum);
    const candidates = ['#000000', '#FFFFFF'].flatMap(target => {
        if (contrastOn(target, failing) < minimum) return [];

        let low = 0;
        let high = 1;
        for (let iteration = 0; iteration < 16; iteration += 1) {
            const amount = (low + high) / 2;
            const candidate = compositeColor(alpha(target, amount), base);
            if (contrastOn(candidate, failing) >= minimum) high = amount;
            else low = amount;
        }

        const candidate = compositeColor(alpha(target, high), base);
        return contrastOn(candidate, backgrounds) >= minimum ? [{color: candidate, amount: high}] : [];
    });

    if (candidates.length) return candidates.sort((a, b) => a.amount - b.amount)[0].color;

    return [base, '#000000', '#FFFFFF'].sort((a, b) => contrastOn(b, backgrounds) - contrastOn(a, backgrounds))[0];
};

export const foregroundOn = (fill: string): string => (
    getContrastRatio('#FFFFFF', fill) > getContrastRatio('#121212', fill) ? '#FFFFFF' : '#121212'
);

export const DEFAULT_PANEL_COLOR = '#FFFFFF';

export const panelColors = (panel: string): SigningPanelColors => {
    const background = compositeColor(panel, '#FFFFFF');
    const text = foregroundOn(background);
    return {
        background,
        text,
        textSecondary: contrastColor(alpha(text, 0.78), [background], 4.55),
        rule: alpha(text, 0.22),
    };
};

export const signingPanel = (theme: Theme): SigningPanelColors => (
    theme.signingPanel ?? panelColors(DEFAULT_PANEL_COLOR)
);

export const signingControls = (theme: Theme): SigningControlColors => theme.signingControls ?? {
    border: theme.palette.divider,
    hoverBorder: theme.palette.primary.main,
    accent: theme.palette.primary.main,
    focus: theme.palette.primary.main,
    fill: theme.palette.primary.main,
    onFill: theme.palette.primary.contrastText,
};
